import { DateTime } from 'luxon';
import { useFrontendTool } from '@copilotkit/react-core';
import { Card, Stack, Table, Text, Title } from '@mantine/core';

import { DataWidget } from 'types/dashboard_widget';

import { DataPoolName } from 'dashboards/types/data_pool';
import { DataWidgetFiltersInput, FilterInput } from 'dashboards/types/filter';

import { DATA_POOLS } from 'dashboards/services/data_pool';
import { useDataGrid } from 'dashboards/hooks/useDataGrid';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';

import { AddToDashboardMenu } from './AddToDashboardMenu';

type QueryDataTableArgs = {
  dataPool: DataPoolName;
  columns: string;
  timeRangeStart: string;
  timeRangeStop: string;
  orderByColumn?: string;
  sort?: 'ASC' | 'DESC';
  filters?: string;
};

type QueryDataTableRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: QueryDataTableArgs;
};

function safeJsonParse<T>(json: string | T | undefined, fallback: T): T {
  if (!json) return fallback;
  if (typeof json !== 'string') return json;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

function DataTableRenderer({
  dataPool,
  columns,
  orderByColumn,
  sort,
  timeRangeStart,
  timeRangeStop,
  filters,
}: {
  dataPool: DataPoolName;
  columns: string[];
  orderByColumn: string;
  sort: 'ASC' | 'DESC';
  timeRangeStart: string;
  timeRangeStop: string;
  filters: FilterInput[];
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const enabled = Boolean(dataPool && columns.length > 0 && timeRangeStart && timeRangeStop);

  const { data, isLoading, error } = useDataGrid({
    params: {
      dataPool: { name: dataPool },
      timeRange: { start: timeRangeStart, stop: timeRangeStop },
      timeZone: timezone,
      columns,
      orderByColumn,
      sort,
      filters,
    },
    enabled,
  });

  if (error) {
    return <ChartError error={error.message} />;
  }

  if (isLoading || !enabled) {
    return <ChartLoader />;
  }

  if (!data) {
    return <ChartError error="No data available." />;
  }

  const { headers, rows } = data;

  if (rows.length === 0) {
    return <ChartError error="No data available for this time range." />;
  }

  return (
    <div className="max-h-64 overflow-auto">
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {headers.map((header) => (
              <Table.Th key={header}>{header}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, index) => (
            <Table.Tr key={index}>
              {row.map((cell, cellIndex) => (
                <Table.Td key={cellIndex}>{cell}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}

function QueryDataTableRendererWrapper({ status, args }: QueryDataTableRenderProps) {
  const {
    dataPool,
    columns: columnsJson,
    timeRangeStart,
    timeRangeStop,
    orderByColumn = 'timestamp',
    sort = 'DESC',
    filters: filtersJson,
  } = args;

  const columns: string[] = safeJsonParse(columnsJson, []);
  const filters: FilterInput[] = safeJsonParse(filtersJson, []);

  // Convert FilterInput to DataWidgetFiltersInput (adds id field)
  const dataWidgetFilters = filters.map((f: FilterInput) => ({
    ...f,
    id: crypto.randomUUID(),
  })) as DataWidgetFiltersInput[];

  const createWidget = () => {
    const defaultLayout = { x: 0, y: 0, w: 6, h: 4 };

    return new DataWidget<'table'>(
      crypto.randomUUID(),
      `${DATA_POOLS[dataPool]?.label || dataPool} Table`,
      'table',
      {
        dataPool: { name: dataPool },
        columns,
        orderByColumn,
        sort,
        filters: dataWidgetFilters.length > 0 ? dataWidgetFilters : undefined,
      },
      defaultLayout
    );
  };

  if (status === 'inProgress' || status === 'executing') {
    return (
      <Card withBorder p="md" radius="md">
        <ChartLoader />
      </Card>
    );
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatDateTime = (iso: string) =>
    DateTime.fromISO(iso).setZone(timezone).toLocaleString(DateTime.DATETIME_SHORT);

  const tableLabel = DATA_POOLS[dataPool]?.label || dataPool;

  return (
    <Card withBorder p="md" radius="md" className="relative">
      <Stack gap="sm">
        <Title order={5}>{tableLabel}</Title>
        <Text size="xs" c="dimmed">
          {formatDateTime(timeRangeStart)} → {formatDateTime(timeRangeStop)} ({timezone})
        </Text>
        <DataTableRenderer
          dataPool={dataPool}
          columns={columns}
          orderByColumn={orderByColumn}
          sort={sort}
          timeRangeStart={timeRangeStart}
          timeRangeStop={timeRangeStop}
          filters={filters}
        />
      </Stack>
      <div className="absolute bottom-2 right-2">
        <AddToDashboardMenu createWidget={createWidget} />
      </div>
    </Card>
  );
}

export function useQueryDataTableTool() {
  const now = new Date().toISOString();

  // Build data pool descriptions for the tool
  const dataPoolDescriptions = Object.entries(DATA_POOLS)
    .map(([name, pool]) => `"${name}" (${pool.label}): columns [${pool.columns.map(c => c.name).join(', ')}]`)
    .join('; ');

  useFrontendTool(
    {
      name: 'queryDataTable',
      description: `Query raw data from analytics data pools and display as a table. Use this to show detailed event/object/node metadata records. Available data pools: ${dataPoolDescriptions}. Current date/time: ${now}.`,
      parameters: [
        {
          name: 'dataPool',
          type: 'string',
          description:
            'The data pool to query. Options: "DP_event_metadata" (events), "DP_object_metadata" (detected objects), "DP_node_metadata" (pipeline node data), "DP_gateway_metrics" (gateway metrics)',
          required: true,
        },
        {
          name: 'columns',
          type: 'string',
          description:
            'JSON array of column names to display. Must be valid columns for the selected data pool. Example: \'["timestamp", "source_name", "event_type"]\'',
          required: true,
        },
        {
          name: 'timeRangeStart',
          type: 'string',
          description: 'Start of time range in ISO 8601 format (e.g., "2024-01-01T00:00:00Z")',
          required: true,
        },
        {
          name: 'timeRangeStop',
          type: 'string',
          description: 'End of time range in ISO 8601 format (e.g., "2024-01-31T23:59:59Z")',
          required: true,
        },
        {
          name: 'orderByColumn',
          type: 'string',
          description: 'Column to order results by. Defaults to "timestamp".',
          required: false,
        },
        {
          name: 'sort',
          type: 'string',
          description: 'Sort order: "ASC" or "DESC". Defaults to "DESC".',
          required: false,
        },
        {
          name: 'filters',
          type: 'string',
          description:
            'JSON array of filter objects with {column, operator, value} for filtering data. Operators: "=", "!=", "<", ">", "<=", ">=", "LIKE"',
          required: false,
        },
      ],
      handler: async (args) => {
        return { ...args, message: 'Data table displayed to user' };
      },
      render: ({ status, args }) => (
        <QueryDataTableRendererWrapper
          status={status}
          args={args as QueryDataTableArgs}
        />
      ),
    },
    []
  );
}
